<script lang="ts" setup>
  import { ref, onMounted, computed, type Ref, nextTick, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import axios from 'axios'

  import SvgIcon from '@shared/components/svg-icon.vue'
  import notificationMessage from '@shared/components/ui/c-notification'
  import Popup from '@shared/components/popup.vue'
  import CBtn from '@shared/components/ui/c-btn.vue'
  import UserIcon from '@shared/components/user-icon.vue'
  import CSelect from '@shared/components/ui/c-select.vue'
  import SnapshotPreview from '@shared/components/meeting/snapshot-preview.vue'
  import SnapshotPreviewPopup from '@shared/components/meeting/snapshot-preview-popup.vue'
  import MeetingPeerVideo from '@shared/components/meeting/meeting-peer-video.vue'
  import Sidebar from '@shared/components/sidebar/sidebar.vue'
  import SnapshotsSidebar from '@shared/components/sidebar/snapshots-sidebar.vue'
  import MobileSnapshotsViewer from '@shared/components/mobile-snapshots-viewer.vue'

  import { ENotificationType } from '@shared/types/constants/notification-type'
  import type IVisitVideoConference from '@shared/api/visits/resources/admin/visit-video-conference'
  import { EMeetingRole } from '@shared/types/constants/meeting-role'
  import type { IOption } from '@shared/types/select'
  import { EDocumentType } from '@shared/api/visits/resources/documents/constants/document-type'
  import type { IVisitDocumentSnapshot } from '@internal/types/visits/visit-files/visit-document-snapshot'
  import { EHttpStatusCode } from '@shared/types/constants/status-code'

  import {
    type HMSPreviewConfig,
    type HMSAudioTrackSettings,
    type HMSVideoTrackSettings,
    type HMSChangeTrackStateRequestNotification,
    type HMSStore,
    type IStoreReadOnly,
    type IHMSNotifications,
    type HMSPeerWithMuteStatus,
    type HMSPeer,
    type DeviceMap,
    type HMSVideoTrack,
    type HMSTrackID,
    type HMSMediaSettings,
    type HMSChangeMultiTrackStateParams,
    HMSNotificationTypes,
    DeviceType,
    selectPeers,
    selectVideoTrackByID,
    selectIsLocalAudioEnabled,
    selectIsLocalVideoEnabled,
    selectLocalMediaSettings,
    selectIsConnectedToRoom,
    selectRecordingState,
    selectDevices,
    selectLocalPeer,
    selectPeersWithAudioStatus,
    selectIsPeerVideoEnabled,
    selectRemotePeers,
    selectIsPeerAudioEnabled,
    selectIsLocalVideoPluginPresent,
  } from '@100mslive/hms-video-store'
  import { HMSVirtualBackgroundPlugin } from '@100mslive/hms-virtual-background'

  import { useAbsoluteDragNDrop } from '@shared/utils/composables/absolute-dran-n-drop'
  import vClickOutside from '@shared/utils/directives/click-outside'
  import { useComposition } from '@shared/utils/composables/meeting'
  import { useResizeHandler } from '@shared/utils/composables/resize-handler'
  import { createImageSrcFromVideo } from '@shared/utils/helpers'
  import {
    deleteVisitDocument,
    createDocumentPresignedUrl,
    createVisitDocument,
    getVisitDocumentsList,
    getUploadedFile,
  } from '@shared/api/visits/documents'
  import { formatDate } from '@shared/utils/date-format'
  import { EDateFormat } from '@shared/types/constants/date-format'
  import { replaceApi } from '@shared/types/constants'
	import { updateVisitStatus } from '@shared/api/visits/admin'
	import { EVisitStatus } from '@shared/api/visits/resources/admin/constants/visit-status'

  const props = withDefaults(
    defineProps<{
      localRole: string
      patientName?: string
      videoConference?: IVisitVideoConference | null
    }>(),
    {
      localRole: EMeetingRole.PATIENT,
      patientName: '',
      videoConference: null,
    }
  )

  const route = useRoute()

  const emit = defineEmits([
    'on-leave',
    'on-guest-leave',
    'on-redirect-preview',
    'on-pip',
    'on-exit-pip',
    'on-invite-contact',
    'on-cancel-visit',
    'on-guest-cancel-visit',
  ])

  const virtualBackground = new HMSVirtualBackgroundPlugin('blur')
  const { previewConfig, isShownMeetingRoom, hmsManager, hmsActions, isPinPMode } = useComposition()
  const { mdAndDown, mdAndUp }: { mdAndDown: Ref<boolean>; mdAndUp: Ref<boolean> } = useResizeHandler()

  const isShowRecordingPopup = ref<boolean>(false)
  const isShowMediaSettingsPopup = ref<boolean>(false)
  const isShowRequestMicPopup = ref<boolean>(false)
  const isShowRequestCameraPopup = ref<boolean>(false)

  const isShowLeaveMeetingPopup = ref<boolean>(false)
  const leaveMeetingPopupCaption = computed(() => {
    return props.localRole === EMeetingRole.HOST
      ? "Press 'End Call for All' to end the video visit. If you need to leave and come back, use the 'Leave Meeting' button. You will exit, but the other participants will remain on the call and you can rejoin again."
      : 'Others will continue after you leave. You can join the call again.'
  })

  const hmsStore: IStoreReadOnly<HMSStore> = hmsManager.getStore()
  const hmsNotfiication: IHMSNotifications = hmsManager.getNotifications()

  const peersContainer = ref<HTMLElement>()
  const hostContainer = ref<HTMLElement>()
  const renderedPeers = ref<HMSPeer[]>([])
  const isConnected = ref<boolean>(false)

  const localPeer = computed(() => hmsStore.getState(selectLocalPeer))

  const isParticipant = computed(() => {
    return Boolean(renderedPeers.value.find(peer => peer.roleName !== localPeer.value?.roleName))
  })

  const hostName = computed(() => {
    return renderedPeers.value.find(peer => peer.roleName === EMeetingRole.HOST)?.name
  })

  const participantName = computed(() => {
    return props.localRole === EMeetingRole.HOST ? props.patientName : hostName.value
  })

  const participantRole = computed(() => {
    return props.localRole === EMeetingRole.HOST ? 'Patient' : 'General Practitioner'
  })

  const loadingSubtitle = computed(() => {
    return props.localRole === EMeetingRole.HOST ? 'Waiting on patient...' : 'Waiting on clinician...'
  })

  const isHostElement = computed(() => {
    return localPeer.value?.roleName === EMeetingRole.HOST
  })

  const isPinPModeButton = computed(() => {
    return [EMeetingRole.HOST].includes(localPeer.value?.roleName as EMeetingRole)
  })

  const is1on1Meeting = computed(() => {
    return memberCount.value <= 2
  })

  const memberCount = computed(() => renderedPeers.value.length)

  const mainContainerStyles = computed(() => {
    return is1on1Meeting.value
      ? 'tw-absolute tw-h-[calc(100dvh-48px)] tw-w-full md:tw-h-full md:tw-max-h-unset md:tw-static md:tw-pt-24 md:tw-flex main-container_1n1'
      : 'md:tw-max-h-[610px] md:tw-max-w-[1080px] tw-overflow-hidden md:tw-flex-1 md:tw-mx-auto main-container_multiple'
  })

  const peersContainerStyles = computed(() => {
    return is1on1Meeting.value
      ? 'tw-z-10 tw-w-[45%] tw-max-w-[235px] tw-top-8 tw-left-8 tw-absolute peer-container_1n1 hover:tw-cursor-move tw-touch-none'
      : 'tw-h-[192px] tw-w-full tw-p-6 tw-flex tw-gap-4 tw-max-w-[1080px] tw-mx-auto tw-overflow-x-auto peer-container_multiple'
  })

  const controlsContainerStyles = computed(() => {
    return is1on1Meeting.value ? 'tw-z-10 tw-mt-auto md:tw-pt-12' : 'tw-mt-auto tw-pt-12'
  })

  const unsubscribeNotification = hmsNotfiication.onNotification(
    notification => {
      switch (notification.type) {
        case HMSNotificationTypes.ERROR:
          return notificationMessage.open({
            message: notification.data.message,
            type: ENotificationType.ERROR,
          })
        case HMSNotificationTypes.CHANGE_TRACK_STATE_REQUEST:
          return changeStateRequestMedia(notification)
        case HMSNotificationTypes.REMOVED_FROM_ROOM:
        case HMSNotificationTypes.ROOM_ENDED:
          return leaveCall()
      }
    },
    [
      HMSNotificationTypes.ERROR,
      HMSNotificationTypes.CHANGE_TRACK_STATE_REQUEST,
      HMSNotificationTypes.REMOVED_FROM_ROOM,
      HMSNotificationTypes.ROOM_ENDED,
    ]
  )
  const changeStateRequestMedia = async ({ data }: HMSChangeTrackStateRequestNotification) => {
    const localPeer = hmsStore.getState(selectLocalPeer)
    switch (data.track.type) {
      case 'video':
        if (localPeer?.videoTrack !== data.track.id) {
          return
        }
        if (data.enabled) {
          return (isShowRequestCameraPopup.value = true)
        } else {
          return await hmsActions.setLocalVideoEnabled(data.enabled)
        }
      case 'audio':
        if (data.enabled) {
          return (isShowRequestMicPopup.value = true)
        } else {
          return await hmsActions.setLocalAudioEnabled(data.enabled)
        }
    }
  }

  const requestCameraPopupHandler = async () => {
    await hmsActions.setLocalVideoEnabled(true)
    updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(localPeer.value?.videoTrack)))
    isShowRequestCameraPopup.value = false
  }

  const requestMicPopupHandler = async () => {
    await hmsActions.setLocalAudioEnabled(true)
    isShowRequestMicPopup.value = false
  }

  const initMeeting = async (newConfig: HMSPreviewConfig) => {
    await hmsActions.join(newConfig)
  }
  const mainPeer = ref<HMSPeer | null>(null)

  const updatePeerVideoState = async (track: HMSVideoTrack | null) => {
    const localPeer = hmsStore.getState(selectLocalPeer)
    if (!track) {
      return
    }
    const videoElement = document.getElementById(track.id) as HTMLVideoElement
    if (!videoElement) {
      return
    }
    if (track?.enabled) {
      videoElement.classList.remove('-tw-scale-x-100')
      if ((!track?.facingMode || track.facingMode === 'user') && track.peerId === localPeer?.id) {
        videoElement.classList.add('-tw-scale-x-100')
      }
      await hmsActions.detachVideo(track.id, videoElement)
      await hmsActions.attachVideo(track.id, videoElement)
    } else {
      await hmsActions.detachVideo(track.id, videoElement)
    }
    videoElement?.load()
  }

  const guestPeers = computed(() => {
    return renderedPeers.value.filter(peer => peer.id !== mainPeer.value?.id)
  })

  const isRecorderPeer = (peer: HMSPeer): boolean => {
    return Boolean(peer.roleName === EMeetingRole.HOST && peer.name === 'Beam')
  }

  const isSkippablePeer = (peer: HMSPeer): boolean => {
    return Boolean(
      isRecorderPeer(peer) || renderedPeers.value.find(renderedPeer => renderedPeer.id === peer.id) || !peer.videoTrack
    )
  }

  const isHighlighted = (peer: HMSPeer) => {
    return Boolean(isHostElement.value && peer.roleName === EMeetingRole.PATIENT && !is1on1Meeting.value)
  }

  const participantRoleName = computed(() => {
    return localPeer.value?.roleName === EMeetingRole.HOST ? EMeetingRole.PATIENT : EMeetingRole.HOST
  })

  const isPossiblyMainPeer = (peer: HMSPeer): boolean => {
    return Boolean(
      peer.roleName === participantRoleName.value ||
        (mainPeer.value?.roleName !== participantRoleName.value &&
          (peer.roleName !== localPeer.value?.roleName || !mainPeer.value))
    )
  }

  const renderPeers = () => {
    const peers: HMSPeer[] = hmsStore.getState(selectPeers)
    peers.forEach(peer => {
      if (isSkippablePeer(peer)) {
        return
      }
      if (isPossiblyMainPeer(peer)) {
        mainPeer.value = peer
      }
      renderedPeers.value.push(peer)
    })
    renderedPeers.value = renderedPeers.value.filter(peer => filterDisconnectedPeers(peers, peer))
  }

  const filterDisconnectedPeers = (peerList: HMSPeer[], peer: HMSPeer) => {
    if (!peerList.find(el => el.id === peer.id)) {
      if (peer.id === mainPeer.value?.id) {
        replaceMainPeer(peerList)
      }
      return false
    }
    return true
  }

  watch(guestPeers, (newValue, oldValue) => {
    nextTick(() => {
      const updatedGuestPeerIdList = newValue.map(item => item.id)
      const guestPeerIdList = oldValue.map(item => item.id)
      const diffNewList = newValue.filter(item => !guestPeerIdList.includes(item.id))
      const diffOldList = oldValue.filter(item => !updatedGuestPeerIdList.includes(item.id))
      diffNewList.concat(diffOldList).forEach(async peer => {
        await updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(peer.videoTrack)))
      })
    })
  })

  watch(mainPeer, newValue => {
    nextTick(async () => {
      if (newValue) {
        await updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(newValue.videoTrack)))
      }
    })
  })

  const parent = ref()
  const removeListeners = ref<() => void>()

  const initPinPMode = () => {
    if (!peersContainer.value || !parent.value) {
      return
    }
    const { onPointerDownHandler, onChangeScreenHandler, onTouchStartHandler, normalizePosition } =
      useAbsoluteDragNDrop({
        parent: parent.value,
        child: peersContainer.value,
        paddingX: 8,
        paddingY: 8,
      })
    peersContainer.value.addEventListener('pointerdown', onPointerDownHandler)
    peersContainer.value.addEventListener('touchstart', onTouchStartHandler)
    window.addEventListener('resize', onChangeScreenHandler)
    document.addEventListener('orientationchange', normalizePosition)
    normalizePosition()
    return () => {
      if (!peersContainer.value) {
        return
      }
      peersContainer.value.removeEventListener('pointerdown', onPointerDownHandler)
      peersContainer.value.removeEventListener('touchstart', onTouchStartHandler)
      window.removeEventListener('resize', onChangeScreenHandler)
      document.removeEventListener('orientationchange', normalizePosition)
      peersContainer.value.style.setProperty('left', 'unset')
      peersContainer.value.style.setProperty('top', 'unset')
      peersContainer.value.style.setProperty('bottom', 'unset')
      peersContainer.value.style.setProperty('right', 'unset', 'important')
    }
  }

  watch(isPinPMode, () => {
    nextTick(() => {
      if (!mainPeer.value) {
        return
      }
      updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(mainPeer.value.videoTrack)))
      if (is1on1Meeting.value) {
        if (isPinPMode.value) {
          removeListeners.value && removeListeners.value()
        } else {
          removeListeners.value && removeListeners.value()
          removeListeners.value = initPinPMode()
        }
      }
    })
  })

  watch(is1on1Meeting, () => {
    if (!isPinPMode.value) {
      if (is1on1Meeting.value) {
        removeListeners.value && removeListeners.value()
        removeListeners.value = initPinPMode()
      } else {
        removeListeners.value && removeListeners.value()
      }
    }
  })

  const replaceMainPeer = (peerList: HMSPeer[]) => {
    const filteredPeerList = peerList.filter(peer => peer.name !== 'Beam')
    const newMainPeer =
      filteredPeerList.find(newPeer => isPossiblyMainPeer(newPeer)) ||
      filteredPeerList.find(newPeer => !newPeer.isLocal) ||
      filteredPeerList[0]
    mainPeer.value = newMainPeer
    if (mainPeer.value?.videoTrack) {
      updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(mainPeer.value.videoTrack)))
    }
  }

  // audio on/off control at the bottom controls
  const enabledLocalAudio = ref<boolean>(false)
  const localAudioButtonText = computed((): string => (enabledLocalAudio.value ? 'Mute' : 'Unmute'))
  const localAudioButtonIcon = computed((): string => (enabledLocalAudio.value ? 'microphone-on' : 'microphone-off'))
  const localAudioButtonStyle = computed((): string => (enabledLocalAudio.value ? 'tw-bg-white' : 'tw-bg-grey-110'))

  const toggleAudio = async (): Promise<void> => {
    await hmsActions.setLocalAudioEnabled(!enabledLocalAudio.value)
  }

  const updateLocalAudioState = (audioState: boolean) => {
    enabledLocalAudio.value = audioState
  }

  const updateLocalVideoState = (videoState: boolean) => {
    enabledLocalVideo.value = videoState
  }

  // video on/off control at the bottom controls

  const enabledLocalVideo = ref<boolean>(false)
  const localVideoButtonText = computed((): string => (enabledLocalVideo.value ? 'Stop video' : 'Start video'))
  const localVideoButtonIcon = computed((): string => (enabledLocalVideo.value ? 'camera-on' : 'camera-off'))
  const localVideoButtonStyle = computed((): string => (enabledLocalVideo.value ? 'tw-bg-white' : 'tw-bg-grey-110'))
  const localVideoDeviceList = computed((): MediaDeviceInfo[] => {
    return localMediaDeviceList.value ? localMediaDeviceList.value[DeviceType.videoInput] : []
  })

  const toggleVideo = async (): Promise<void> => {
    await hmsActions.setLocalVideoEnabled(!enabledLocalVideo.value)
    updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(localPeer.value?.videoTrack)))
  }

  // flip camera (mobile only) at the bottom controls
  const toggleDevice = (): void => {
    const selectLocalMediaSettingsqwe = hmsStore.getState(selectLocalMediaSettings)
    const newVideoDevice = localVideoDeviceList.value.find(
      el => el.deviceId !== selectLocalMediaSettingsqwe.videoInputDeviceId
    )
    updateLocalVideoSettings(newVideoDevice?.deviceId)
  }

  // leave call handler
  const leaveCall = async (): Promise<void> => {
    hmsActions?.setLocalVideoEnabled(false)
    unsubscribeNotification()
    await hmsActions?.leave()
    if (localPeer.value?.roleName === EMeetingRole.GUEST) {
      emit('on-guest-leave')
    } else {
      emit('on-leave')
    }
    isShownMeetingRoom.value = false
  }

  // end call for all

  const endCall = async (): Promise<void> => {
    try {
      await hmsActions.setLocalVideoEnabled(false)
      unsubscribeNotification()
      await hmsActions.endRoom(true, 'Call is ended')
			await updateVisitStatus({
        visitId: route.params.visitId,
        visitStatus: EVisitStatus.CALL_ENDED,
      })
      await emit('on-leave')
      isShownMeetingRoom.value = false
    } catch {
      notificationMessage.open({
        message: 'Permission denied or not connected to room',
        type: ENotificationType.ERROR,
      })
    }
  }

  // condition for displaying 'flip camera' button
  const isMultipleDevices = computed((): boolean => {
    return localMediaDeviceList.value ? localMediaDeviceList.value[DeviceType.videoInput].length > 1 : false
  })

  const updateLocalMediaDeviceList = (mediaDevices: DeviceMap): void => {
    localMediaDeviceList.value = mediaDevices
  }

  // recodring popup handler
  const recordingInProgress = ref<boolean>(false)
  const popupRecordingCancelHandler = async (): Promise<void> => {
    if (!isShowRecordingPopup.value) {
      return
    }
    switch (localPeer.value?.roleName) {
      case EMeetingRole.PATIENT: {
        await emit('on-cancel-visit')
        break
      }
      case EMeetingRole.GUEST: {
        await emit('on-guest-cancel-visit')
        break
      }
    }
    isShowRecordingPopup.value = false
    unsubscribeNotification()
    await hmsActions.leave()
    isShownMeetingRoom.value = false
  }
  const popupRecordingSubmitHandler = (): void => {
    isShowRecordingPopup.value = false
  }
  const recordingHandler = (): void => {
    const recordingState = hmsStore.getState(selectRecordingState)
    if (recordingState.browser.running !== recordingInProgress.value) {
      recordingInProgress.value = recordingState.browser.running
      isShowRecordingPopup.value = recordingState.browser.running && localPeer.value?.roleName !== EMeetingRole.HOST
    }
  }

  const getMeetingUrlForRecording = ({ startLink }: { startLink?: string }): string => {
    return startLink?.replaceAll('meeting', 'preview') + '?skip_preview=true' || ''
  }

  const recordingButtonCaption = computed(() => {
    return recordingInProgress.value ? 'Stop recording' : 'Start recording'
  })

  const startRecording = async () => {
    const params = {
      meetingURL: getMeetingUrlForRecording({ startLink: props.videoConference?.startLink }),
      record: true,
    }
    try {
      await hmsActions.startRTMPOrRecording(params)
      notificationMessage.open({
        message: 'RTMP/recording has been started',
        type: ENotificationType.INFO,
      })
    } catch (err) {
      notificationMessage.open({
        message: 'failed to start RTMP/recording',
        type: ENotificationType.ERROR,
      })
    }
  }

  const stopRecording = async () => {
    try {
      await hmsActions.stopRTMPAndRecording()
      notificationMessage.open({
        message: 'RTMP/recording has been stopped',
        type: ENotificationType.INFO,
      })
    } catch (err) {
      notificationMessage.open({
        message: 'failed to stop RTMP/recording',
        type: ENotificationType.ERROR,
      })
    }
  }

  const toggleRecording = () => {
    isShownAdditionalSettings.value = false
    recordingInProgress.value ? stopRecording() : startRecording()
  }

  // change local media device

  const localMediaDeviceList = ref<DeviceMap>()
  const localMediaSettings = ref<HMSMediaSettings>(hmsStore.getState(selectLocalMediaSettings))

  const updateLocalMediaSettings = (mediaSettings: HMSMediaSettings): void => {
    localMediaSettings.value = mediaSettings
  }

  const formattedLocalVideoDeviceList = computed((): IOption[] => {
    return localMediaDeviceList.value
      ? localMediaDeviceList.value[DeviceType.videoInput]
          .map((device: MediaDeviceInfo) => {
            return { id: device.deviceId, label: device.label, value: device.deviceId }
          })
          .filter(device => device.id !== 'default')
      : []
  })

  const formattedLocalAudioInDeviceList = computed((): IOption[] => {
    return localMediaDeviceList.value
      ? localMediaDeviceList.value[DeviceType.audioInput]
          .map((device: MediaDeviceInfo) => {
            return { id: device.deviceId, label: device.label, value: device.deviceId }
          })
          .filter(device => device.id !== 'default')
      : []
  })

  const formattedLocalAudioOutDeviceList = computed((): IOption[] => {
    return localMediaDeviceList.value
      ? localMediaDeviceList.value[DeviceType.audioOutput]
          .map((device: MediaDeviceInfo) => {
            return { id: device.deviceId, label: device.label, value: device.deviceId }
          })
          .filter(device => device.id !== 'default')
      : []
  })

  const peersWithAudioStatus = ref<HMSPeerWithMuteStatus[]>()

  const updatePeersWithAudioStatus = (peers: HMSPeerWithMuteStatus[]) => {
    peersWithAudioStatus.value = peers
  }

  const getAudioState = (peer: HMSPeer) => {
    const peerAudioState = peersWithAudioStatus.value?.find(el => el.peer.id === peer.id)
    return peerAudioState?.isAudioEnabled
  }

  hmsStore.subscribe(() => {
    isConnected.value = true
  }, selectIsConnectedToRoom)

  onMounted(async () => {
    if (!previewConfig.value) {
      notificationMessage.open({
        message: 'Something went wrong. Please try again',
        type: ENotificationType.ERROR,
      })
      emit('on-redirect-preview')
      isShownMeetingRoom.value = false
      return
    }

    initMeeting(previewConfig.value)
    localMediaDeviceList.value = hmsStore.getState(selectDevices)
    enabledLocalVideo.value = hmsStore.getState(selectIsLocalVideoEnabled)
    enabledLocalAudio.value = hmsStore.getState(selectIsLocalAudioEnabled)
    hmsStore.subscribe(renderPeers, selectPeers)
    hmsStore.subscribe(updateLocalMediaDeviceList, selectDevices)
    hmsStore.subscribe(updateLocalMediaSettings, selectLocalMediaSettings)
    // listener for meeting recording
    hmsStore.subscribe(recordingHandler, selectRecordingState)
    // listener for micro state
    hmsStore.subscribe(updateLocalAudioState, selectIsLocalAudioEnabled)
    // listener for camera state
    hmsStore.subscribe(updateLocalVideoState, selectIsLocalVideoEnabled)
    hmsStore.subscribe(updatePeersWithAudioStatus, selectPeersWithAudioStatus)
    setTimeout(() => {
      if (props.localRole === EMeetingRole.HOST) {
        if (!recordingInProgress.value) {
          startRecording()
        }
        toggleVB()
      }
    }, 1000)

    if (is1on1Meeting.value) {
      removeListeners.value && removeListeners.value()
      removeListeners.value = initPinPMode()
    }
    if (props.localRole === EMeetingRole.HOST) {
      const respSnapshotList = await getSnapshotList()
      snapshotList.value = respSnapshotList?.length ? respSnapshotList : []
    }
  })

  const updateLocalVideoSettings = async (deviceId: HMSVideoTrackSettings['deviceId']) => {
    await hmsActions.setVideoSettings({ deviceId })
    updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(localPeer.value?.videoTrack)))
    closeSettingsPopup()
    isShowMediaSettingsPopup.value = false
  }

  const updateLocalAudioInputSettings = async (deviceId: HMSAudioTrackSettings['deviceId']) => {
    await hmsActions.setAudioSettings({ deviceId })
    closeSettingsPopup()
    isShowMediaSettingsPopup.value = false
  }

  const updateLocalAudioOutputSettings = async (deviceId: string) => {
    await hmsActions.setAudioOutputDevice(deviceId)
    closeSettingsPopup()
    isShowMediaSettingsPopup.value = false
  }
  const isShownSettingsPanel = ref<boolean>(false)

  const closeSettingsPopup = () => {
    isShownSettingsPanel.value = false
    isShownAdditionalSettingsPanel.value = false
  }

  //openAdditionalSettingsPanel

  const toggleSettingsPopup = () => {
    isShownAdditionalSettings.value = false
    if (mdAndDown.value) {
      isShowMediaSettingsPopup.value = true
    } else {
      isShownSettingsPanel.value = !isShownSettingsPanel.value
      isShownAdditionalSettingsPanel.value = !isShownAdditionalSettingsPanel.value
    }
  }

  const getSnapshotList = async () => {
    if (props.localRole !== EMeetingRole.HOST) {
      return
    }
    try {
      const { visitId } = route.params
      const documentsList = await getVisitDocumentsList({ visitId })
      const filteredDocumentList: IVisitDocumentSnapshot[] = documentsList.filter(
        document => document.documentType === EDocumentType.SNAPSHOT
      )
      filteredDocumentList.forEach(async document => {
        const url = document.documentUrl.replace(replaceApi, '')
        try {
          await getUploadedFile(url)
        } catch (e: unknown) {
          if (axios.isAxiosError(e) && e?.response?.status === EHttpStatusCode.FOUND && e.response.data.location) {
            document.imageSrc = await handleSnapshotUrl(e.response.data.location)
          }
        }
      })
      return filteredDocumentList || []
    } catch {
      notificationMessage.open({
        message: 'Something went wrong',
        type: ENotificationType.ERROR,
      })
    }
  }

  const handleSnapshotUrl = async (location: string) => {
    try {
      const res = await fetch(location, {
        method: 'GET',
      })
      return await res.text()
    } catch {
      notificationMessage.open({
        message: 'Something went wrong',
        type: ENotificationType.ERROR,
      })
    }
  }

  const pInPModeHandler = async () => {
    await emit('on-pip')
    isPinPMode.value = true
  }
  const closePinPModeHandler = async () => {
    await emit('on-exit-pip')
    isPinPMode.value = false
    const respSnapshotList = await getSnapshotList()
    if (respSnapshotList?.length) {
      snapshotList.value = respSnapshotList
    }
    nextTick(() => {
      guestPeers.value.forEach(peer => {
        updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(peer.videoTrack)))
      })
    })
  }

  // sidepanel functionality

  const isOpenedSidePanel = ref<boolean>(false)
  const toggleSidePanel = () => {
    if (isSnapshotSidebar.value) {
      isSnapshotSidebar.value = false
    }
    isOpenedSidePanel.value = !isOpenedSidePanel.value
  }

  const toggleCameraForPeer = async (peer: HMSPeer) => {
    if (!peer) {
      return
    }
    try {
      const isVideoEnabled = hmsStore.getState(selectIsPeerVideoEnabled(peer.id))
      if (peer.isLocal) {
        return toggleVideo()
      }
      await hmsActions.setRemoteTrackEnabled(peer.videoTrack as HMSTrackID, !isVideoEnabled)
      if (!isVideoEnabled) {
        notificationMessage.open({
          message: 'Request to share video was sent',
          type: ENotificationType.INFO,
        })
      }
    } catch {
      notificationMessage.open({
        message: 'Permission denied or invalid peer ID or not connected to room',
        type: ENotificationType.ERROR,
      })
    }
  }

  const toggleMicForPeer = async (peer: HMSPeer) => {
    if (!peer) {
      return
    }

    try {
      if (peer.isLocal) {
        return toggleAudio()
      }
      const isAudioEnabled = hmsStore.getState(selectIsPeerAudioEnabled(peer.id))
      const updatedPeerList = hmsStore.getState(selectRemotePeers)
      const selectedPeer = updatedPeerList.find(el => el.id === peer.id)
      if (!selectedPeer) {
        return
      }
      await hmsActions.setRemoteTrackEnabled(selectedPeer.audioTrack as HMSTrackID, !isAudioEnabled)
      if (!isAudioEnabled) {
        notificationMessage.open({
          message: 'Request to unmute microphone was sent',
          type: ENotificationType.INFO,
        })
      }
    } catch {
      notificationMessage.open({
        message: 'Permission denied or invalid peer ID or not connected to room',
        type: ENotificationType.ERROR,
      })
    }
  }

  const kickPeer = async (peer: HMSPeer) => {
    if (peer.isLocal) {
      return (isShowLeaveMeetingPopup.value = true)
    }
    try {
      const reason = 'Good Bye'
      await hmsActions.removePeer(peer.id, reason)
      notificationMessage.open({
        message: `${peer.name} has been removed successfully`,
        type: ENotificationType.INFO,
      })
    } catch {
      notificationMessage.open({
        message: 'Permission denied or invalid peer ID or not connected to room',
        type: ENotificationType.ERROR,
      })
    }
  }

  //right sidepanel controls for invite to call & mute all peers at once

  const inviteToCallHandler = async () => {
    await emit('on-invite-contact')
    isOpenedSidePanel.value = false
    isPinPMode.value = true
  }

  const muteAllPeers = async () => {
    try {
      const options: HMSChangeMultiTrackStateParams = {
        enabled: false,
        type: 'audio',
      }
      await hmsActions.setRemoteTracksEnabled(options)
    } catch {
      notificationMessage.open({
        message: 'Permission denied or not connected to room',
        type: ENotificationType.ERROR,
      })
    }
  }

  // virtual BG
  const virtualBgButtonCaption = ref<string>('Blur background')

  async function toggleVB() {
    const isVirtualBackgroundEnabled = hmsStore.getState(selectIsLocalVideoPluginPresent(virtualBackground.getName()))
    try {
      isShownAdditionalSettings.value = false
      if (!isVirtualBackgroundEnabled) {
        const pluginFrameRate = 15
        await hmsActions.addPluginToVideoTrack(virtualBackground, pluginFrameRate)
        virtualBgButtonCaption.value = 'Remove background blur'
      } else {
        await hmsActions.removePluginFromVideoTrack(virtualBackground)
        virtualBgButtonCaption.value = 'Blur background'
      }
      const localPeer = hmsStore.getState(selectLocalPeer)
      updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(localPeer?.videoTrack)))
    } catch {
      notificationMessage.open({
        message: 'Virtual background failure',
        type: ENotificationType.ERROR,
      })
    }
  }

  // snapshot functionality
  const displayedSnapshotList = ref<string[]>([])
  const snapshotList = ref<IVisitDocumentSnapshot[]>([])
  const displaySnapshotFlyout = ref<boolean>(false)
  const displayedSnapshotInterval = ref()
  const cleanDisplayedSnapshot = async () => {
    if (!displayedSnapshotList.value.length) {
      clearInterval(displayedSnapshotInterval.value)
      return (displayedSnapshotInterval.value = null)
    }
    if (!isSnapshotPreview.value) {
      const snapshotForSaving = displayedSnapshotList.value.splice(0, 1)
      const createdSnapshot: IVisitDocumentSnapshot = await handleUploadFile(snapshotForSaving[0])
      createdSnapshot.imageSrc = snapshotForSaving[0]
      snapshotList.value.push(createdSnapshot)
    }
  }
  const handleUploadFile = async (base64Image: string) => {
    const { visitId } = route.params
    const document = await createDocumentPresignedUrl({ visitId })
    await fetch(document.preSignedUrl, {
      method: 'PUT',
      body: base64Image,
    })
    return await createVisitDocument({
      visitId,
      documentData: {
        documentType: EDocumentType.SNAPSHOT,
        documentId: document.documentId,
        description: `Snapshot-${formatDate({ date: new Date().getTime(), format: EDateFormat.SNAPSHOT_NAMING })}`,
      },
    })
  }

  const isActiveSnapshotButton = () => {
    return mainPeer.value && hmsStore.getState(selectIsPeerVideoEnabled(mainPeer.value.id))
  }

  const snapshotButtonTitle = () => {
    return isActiveSnapshotButton() ? '' : 'Is not available. The video is turned off'
  }

  const createSnapshotFromVideo = () => {
    if (!mainPeer?.value?.videoTrack) {
      return
    }
    const imageSrc = createImageSrcFromVideo({ elementId: mainPeer.value.videoTrack })
    flyoutSnapshot(imageSrc)
  }
  const flyoutSnapshot = (imageSrc: string) => {
    if (!imageSrc) {
      return
    }
    displayedSnapshotList.value.push(imageSrc)
    displaySnapshotFlyout.value = true
    if (!displayedSnapshotInterval.value) {
      displayedSnapshotInterval.value = setInterval(cleanDisplayedSnapshot, 5000)
    }
    if (displayedSnapshotList.value.length > 3) {
      cleanDisplayedSnapshot()
    }
  }
  const isSnapshotPreview = ref<boolean>(false)
  const selectedSnapshotForPreview = ref<string>('')
  const openSnapshotPreview = (imageSrc: string) => {
    isSnapshotPreview.value = true
    selectedSnapshotForPreview.value = imageSrc
  }

  const filterNotSavedSnapshot = (deletedImageSrc: string) => {
    displayedSnapshotList.value = displayedSnapshotList.value.filter(snapshot => snapshot !== deletedImageSrc)
    isSnapshotPreview.value = false
  }

  const isShowDeleteSnapshotPopup = ref<boolean>(false)
  const snapshotForDeleting = ref<string | IVisitDocumentSnapshot>()
  const deletePopupCallback = ref<(snapshot: string | IVisitDocumentSnapshot) => void | Promise<void>>()

  const openDeleteSnapshotPopup = (
    snapshot: string | IVisitDocumentSnapshot,
    callback: (snapshot: string | IVisitDocumentSnapshot) => void | Promise<void>
  ) => {
    isShowDeleteSnapshotPopup.value = true
    snapshotForDeleting.value = snapshot
    deletePopupCallback.value = callback
  }

  const onFilterSnapshotHandler = () => {
    if (snapshotForDeleting.value) {
      filterNotSavedSnapshot(snapshotForDeleting.value as string)
    }
    isShowDeleteSnapshotPopup.value = false
  }

  const onCancelSnapshotPopupHandler = () => {
    isShowDeleteSnapshotPopup.value = false
    if (!displayedSnapshotInterval.value) {
      displayedSnapshotInterval.value = setInterval(cleanDisplayedSnapshot, 5000)
    }
  }

  const onDeleteSnapshotPopupHandler = () => {
    if (deletePopupCallback.value && snapshotForDeleting.value) {
      deletePopupCallback.value(snapshotForDeleting.value)
    }
    isShowDeleteSnapshotPopup.value = false
    if (!displayedSnapshotInterval.value) {
      displayedSnapshotInterval.value = setInterval(cleanDisplayedSnapshot, 5000)
    }
  }

  const onDeleteSnapshot = async (snapshot: IVisitDocumentSnapshot) => {
    const { visitId } = route.params
    await deleteVisitDocument({ visitId, documentId: snapshot.documentId })
    snapshotList.value = snapshotList.value.filter(snap => snap.id !== snapshot.id)
  }

  const isSnapshotSidebar = ref<boolean>(false)
  const isMobileSnapshotViewer = ref<boolean>(false)
  const toggleSnapshotSidebar = () => {
    if (isOpenedSidePanel.value) {
      isOpenedSidePanel.value = false
    }
    if (mdAndDown.value) {
      return (isMobileSnapshotViewer.value = !isMobileSnapshotViewer.value)
    }
    isSnapshotSidebar.value = !isSnapshotSidebar.value
  }
  const toggleMobileSnapshotViewer = () => {
    isMobileSnapshotViewer.value = !isMobileSnapshotViewer.value
  }

  const isShownAdditionalSettings = ref<boolean>(false)
  const isShownAdditionalSettingsPanel = ref<boolean>(false)

  const toggleAdditionalSettings = () => {
    if (isShownAdditionalSettingsPanel.value) {
      isShownAdditionalSettings.value = false
    } else {
      isShownAdditionalSettings.value = !isShownAdditionalSettings.value
    }
    isShownAdditionalSettingsPanel.value = false
  }

  const closeAdditionalSetting = () => {
    isShownAdditionalSettings.value = false
  }

  // pin functionality

  const pinnedPeer = ref<HMSPeer | null>(null)
  const isPeerPinned = (peer: HMSPeer) => {
    return Boolean(pinnedPeer.value && peer.id === pinnedPeer.value.id && !is1on1Meeting.value && mdAndUp.value)
  }
  const pinningIsAvaliable = computed(() => {
    return !is1on1Meeting.value && mdAndUp.value && isHostElement.value
  })
  const pinnablePeer = (peer: HMSPeer) => {
    return (
      isHostElement.value && pinningIsAvaliable.value && (!pinnedPeer?.value?.id || pinnedPeer.value?.id !== peer.id)
    )
  }
  const onPinHandler = (peer: HMSPeer) => {
    pinnedPeer.value = pinnedPeer.value && pinnedPeer.value.id === peer.id ? null : peer
    if (pinnedPeer.value) {
      mainPeer.value = pinnedPeer.value
      updatePeerVideoState(hmsStore.getState(selectVideoTrackByID(mainPeer.value.videoTrack)))
    }
  }
</script>

<template>
  <div v-if="!isPinPMode" class="tw-flex tw-w-full tw-h-full">
    <div class="tw-w-full tw-flex tw-flex-col tw-min-w-[calc(100vw-450px)]">
      <header
        class="tw-h-[48px] md:tw-h-[112px] tw-bg-white tw-py-4 md:tw-py-24 tw-px-8 md:tw-px-48 tw-flex tw-items-center tw-justify-between"
      >
        <div class="tw-flex tw-flex-col tw-h-full tw-justify-center">
          <template v-if="isParticipant">
            <span class="tw-text-caption-1 md:tw-text-headline-2 tw-mb-2 md:tw-mb-4">{{ participantName }}</span>
            <span class="tw-text-tab-1 md:tw-text-headline-4 tw-text-grey-600">{{ participantRole }}</span>
          </template>
          <span v-else class="tw-text-caption-1 md:tw-text-headline-2">{{ loadingSubtitle }}</span>
        </div>
        <div class="tw-flex tw-items-center tw-gap-8">
          <div
            v-if="recordingInProgress"
            title="recording in progress"
            class="tw-h-20 tw-w-20 tw-bg-grey-300 tw-rounded-full tw-flex tw-items-center tw-justify-center"
          >
            <svg-icon class="tw-fill-accent-500" name="recording" size="11" />
          </div>

          <button v-if="isHostElement" class="tw-flex tw-items-center tw-justify-center" @click="toggleSidePanel">
            <svg-icon class="tw-fill-primary-100" name="meeting-community" size="24" />
            <sup class="tw-text-caption-3 tw-text-grey-800 tw-font-[600]">{{ memberCount }}</sup>
          </button>

          <button
            v-if="isPinPModeButton"
            class="tw-rounded-4 tw-bg-secondary-100 tw-h-40 tw-w-40 tw-flex tw-items-center tw-justify-center"
            @click="pInPModeHandler"
          >
            <svg-icon class="tw-fill-primary-100" name="collapse" size="24" />
          </button>

          <button
            v-if="!isHostElement"
            class="tw-rounded-4 tw-bg-secondary-100 tw-h-40 tw-w-40 tw-flex tw-items-center tw-justify-center tw-relative"
            click-outside-caller
            @click="toggleSettingsPopup"
          >
            <svg-icon
              class="tw-fill-primary-100"
              click-outside-caller
              name="settings"
              size="20"
              @click.stop="toggleSettingsPopup"
            />
            <div
              v-if="isShownSettingsPanel"
              v-click-outside="closeSettingsPopup"
              class="tw-hidden tw-absolute tw-w-[345px] md:tw-flex tw-flex-col tw-gap-16 tw-p-16 tw-bg-grey-100 tw-shadow-100 tw-z-20 tw-top-[calc(100%+16px)] tw-right-0 tw-rounded-8 tw-text-left"
              @click.stop
            >
              <h4 class="tw-text-headline-4">Device settings</h4>
              <c-select
                :value="localMediaSettings.videoInputDeviceId"
                class="tw-w-full"
                label="Video:"
                :options="formattedLocalVideoDeviceList"
                @change="updateLocalVideoSettings"
              />
              <c-select
                :value="localMediaSettings.audioInputDeviceId"
                class="tw-w-full"
                label="Microphone:"
                :options="formattedLocalAudioInDeviceList"
                @change="updateLocalAudioInputSettings"
              />
              <c-select
                v-if="formattedLocalAudioOutDeviceList.length"
                :value="localMediaSettings.audioOutputDeviceId"
                class="tw-w-full tw-hidden md:tw-block"
                label="Speakers:"
                :options="formattedLocalAudioOutDeviceList"
                @change="updateLocalAudioOutputSettings"
              />
            </div>
          </button>
        </div>
      </header>
      <div
        v-loading="!isConnected"
        class="tw-bg-other-1100 tw-flex-auto tw-flex tw-flex-col tw-overflow-hidden tw-h-full md:tw-h-[100dvh-112px] tw-relative"
      >
        <button
          v-if="isHostElement && snapshotList.length"
          class="tw-absolute tw-z-20 tw-top-24 tw-left-24 md:tw-left-48 tw-flex tw-items-center tw-gap-8"
          @click="toggleSnapshotSidebar"
        >
          <svg-icon class="tw-fill-white" name="snapshot" size="32" />
          <span class="tw-text-subtitle-1 tw-text-white">{{ snapshotList.length }}</span>
        </button>
        <div
          ref="parent"
          class="tw-relative md:tw-h-[calc(100dvh-112px-130px)] flex-auto tw-flex tw-flex-col"
          :class="[is1on1Meeting ? 'tw-h-full' : 'tw-h-[calc(100dvh-48px-130px)]']"
        >
          <div ref="peersContainer" :class="peersContainerStyles">
            <meeting-peer-video
              v-for="peer in guestPeers"
              :id="peer.id"
              :key="peer.id"
              class="tw-h-full tw-relative peer-wrapper tw-max-w-full tw-aspect-[4/3]"
              :class="{ 'tw-border-2 tw-border-primary-100 tw-rounded-8': isHighlighted(peer) }"
              :peer="peer"
              :is-pin-button="pinnablePeer(peer)"
              :is-pinned="isPeerPinned(peer)"
              :is-displayed-peer-name="!is1on1Meeting"
              :is-audio="!!getAudioState(peer)"
              :is-video="hmsStore.getState(selectIsPeerVideoEnabled(peer.id))"
              @on-pin="onPinHandler"
            />
          </div>
          <div v-if="mainPeer" ref="hostContainer" :class="mainContainerStyles">
            <meeting-peer-video
              :id="mainPeer.id"
              :key="mainPeer.id"
              class="peer-wrapper tw-w-full tw-relative tw-h-full"
              :class="{
                'tw-border-2 tw-border-primary-100 md:tw-rounded-8': isHighlighted(mainPeer),
              }"
              :peer="mainPeer"
              :is-pin-button="pinnablePeer(mainPeer)"
              :is-pinned="isPeerPinned(mainPeer)"
              :is-displayed-peer-name="
                !((is1on1Meeting && mainPeer.roleName === EMeetingRole.HOST) || mainPeer.isLocal)
              "
              :is-audio="!!getAudioState(mainPeer)"
              :is-video="hmsStore.getState(selectIsPeerVideoEnabled(mainPeer.id))"
              @on-pin="onPinHandler"
            />
          </div>
        </div>
        <div
          :class="controlsContainerStyles"
          class="tw-static tw-w-full tw-flex tw-px-24 tw-pb-24 tw-justify-between md:tw-justify-center md:tw-gap-48"
        >
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-8">
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center"
              :class="localVideoButtonStyle"
              @click="toggleVideo"
            >
              <svg-icon :name="localVideoButtonIcon" size="40" />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">{{ localVideoButtonText }}</span>
          </div>

          <div class="tw-flex tw-flex-col tw-items-center tw-gap-8">
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center"
              :class="localAudioButtonStyle"
              @click="toggleAudio"
            >
              <svg-icon :name="localAudioButtonIcon" size="40" />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">{{ localAudioButtonText }}</span>
          </div>

          <div v-if="isHostElement" class="tw-flex tw-flex-col tw-items-center tw-gap-8">
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-grey-110 disabled:tw-cursor-not-allowed disabled:tw-opacity-20"
              :disabled="!isActiveSnapshotButton()"
              :title="snapshotButtonTitle()"
              @click="createSnapshotFromVideo"
            >
              <svg-icon name="camera" size="40" class="tw-fill-white" />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">Snapshot</span>
          </div>

          <div
            v-if="isMultipleDevices && !isHostElement"
            class="tw-flex md:tw-hidden tw-flex-col tw-items-center tw-gap-8"
          >
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-grey-110"
              @click="toggleDevice"
            >
              <svg-icon class="tw-fill-white" name="recover" size="40" />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">Flip camera</span>
          </div>

          <div class="tw-flex tw-flex-col tw-items-center tw-gap-8">
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-accent-500"
              @click="isShowLeaveMeetingPopup = true"
            >
              <svg-icon class="tw-fill-white" name="end-call" size="40" />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">End call</span>
          </div>

          <div
            v-if="isHostElement"
            click-outside-caller
            class="tw-flex tw-flex-col tw-items-center tw-gap-8 tw-relative"
          >
            <div
              v-if="isShownAdditionalSettingsPanel"
              v-click-outside="closeSettingsPopup"
              class="tw-hidden tw-absolute tw-w-[345px] md:tw-flex tw-flex-col tw-gap-16 tw-p-16 tw-bg-grey-100 tw-shadow-100 tw-z-20 tw-bottom-[calc(100%+16px)] tw-right-0 tw-rounded-8 tw-text-left"
              @click.stop
            >
              <h4 class="tw-text-headline-4">Device settings</h4>
              <c-select
                :value="localMediaSettings.videoInputDeviceId"
                class="tw-w-full"
                label="Video:"
                :options="formattedLocalVideoDeviceList"
                @change="updateLocalVideoSettings"
              />
              <c-select
                :value="localMediaSettings.audioInputDeviceId"
                class="tw-w-full"
                label="Microphone:"
                :options="formattedLocalAudioInDeviceList"
                @change="updateLocalAudioInputSettings"
              />
              <c-select
                v-if="formattedLocalAudioOutDeviceList.length"
                :value="localMediaSettings.audioOutputDeviceId"
                class="tw-w-full tw-hidden md:tw-block"
                label="Speakers:"
                :options="formattedLocalAudioOutDeviceList"
                @change="updateLocalAudioOutputSettings"
              />
            </div>
            <div
              v-if="isShownAdditionalSettings"
              v-click-outside="closeAdditionalSetting"
              class="tw-absolute tw-bg-grey-500 tw-bottom-[calc(100%+16px)] tw-right-0 tw-rounded-8 tw-w-[280px] tw-shadow-100 tw-flex tw-flex-col tw-gap-1"
            >
              <button
                class="tw-text-button-2 tw-text-primary-100 tw-p-16 tw-bg-grey-100 tw-text-left tw-rounded-t-8 hover:tw-bg-grey-500"
                click-outside-caller
                @click.stop="toggleSettingsPopup"
              >
                Change device settings
              </button>
              <button
                class="tw-text-button-2 tw-text-primary-100 tw-p-16 tw-bg-grey-100 tw-text-left hover:tw-bg-grey-500"
                @click="toggleVB"
              >
                {{ virtualBgButtonCaption }}
              </button>
              <button
                class="tw-text-button-2 tw-text-primary-100 tw-p-16 tw-bg-grey-100 tw-text-left tw-rounded-b-8 hover:tw-bg-grey-500"
                @click="toggleRecording"
              >
                {{ recordingButtonCaption }}
              </button>
            </div>
            <button
              class="tw-h-[60px] tw-w-[60px] tw-rounded-full tw-flex tw-items-center tw-justify-center"
              :class="[isShownAdditionalSettings || isShownAdditionalSettingsPanel ? 'tw-bg-white' : 'tw-bg-grey-110']"
              click-outside-caller
              @click="toggleAdditionalSettings"
            >
              <svg-icon
                name="options"
                size="24"
                click-outside-caller
                :class="
                  isShownAdditionalSettings || isShownAdditionalSettingsPanel ? 'tw-fill-primary-100' : 'tw-fill-white'
                "
              />
            </button>
            <span class="tw-text-caption-3 tw-text-grey-100 tw-font-[600]">More</span>
          </div>
        </div>
      </div>
    </div>

    <sidebar
      v-if="isOpenedSidePanel"
      :title="`Participants (${memberCount})`"
      is-close-button
      @on-close="toggleSidePanel"
    >
      <template #body>
        <ul class="tw-flex tw-p-16 tw-flex-col tw-gap-16 tw-overflow-y-auto">
          <li
            v-for="peer in renderedPeers"
            :key="peer.id"
            class="tw-p-12 tw-border tw-border-grey-400 tw-rounded-4 tw-bg-grey-200 tw-flex tw-gap-16"
          >
            <user-icon
              :full-name="peer.name"
              :width="36"
              :height="36"
              :string-color="peer.id"
              non-active-color="#B1CBE1"
              is-active
            />
            <div class="tw-flex tw-flex-col tw-gap-6 tw-mr-auto">
              <span class="tw-text-subtitle-2">{{ peer.name }}</span>
              <span class="tw-text-placeholder-2 tw-capitalize tw-text-grey-600">{{ peer.roleName }}</span>
            </div>
            <button
              class="tw-rounded-4 tw-bg-secondary-100 tw-h-40 tw-w-40 tw-flex tw-items-center tw-justify-center"
              @click="toggleCameraForPeer(peer)"
            >
              <svg-icon
                class="tw-fill-primary-100"
                :name="hmsStore.getState(selectIsPeerVideoEnabled(peer.id)) ? 'camera-on' : 'camera-off-blue'"
                size="24"
              />
            </button>
            <button
              class="tw-rounded-4 tw-bg-secondary-100 tw-h-40 tw-w-40 tw-flex tw-items-center tw-justify-center"
              @click="toggleMicForPeer(peer)"
            >
              <svg-icon
                class="tw-fill-primary-100"
                :name="getAudioState(peer) ? 'microphone-on' : 'microphone-off-blue'"
                size="24"
              />
            </button>
            <button
              class="tw-rounded-4 tw-bg-secondary-100 tw-h-40 tw-w-40 tw-flex tw-items-center tw-justify-center"
              @click="kickPeer(peer)"
            >
              <svg-icon class="tw-fill-primary-100" name="log-out" size="24" />
            </button>
          </li>
        </ul>
      </template>
      <template #controls>
        <c-btn size="small" class="tw-w-min" @click="inviteToCallHandler">
          <svg-icon name="plus" class="tw-fill-primary-100 tw-mr-10" size="12" />
          Invite new contact to the call
        </c-btn>
        <c-btn size="small" class="tw-w-min" @click="muteAllPeers">
          <template #icon>
            <svg-icon name="microphone-off_plain" size="24" class="tw-fill-primary-100" />
          </template>
          <span class="tw-ml-8"> Mute all </span>
        </c-btn>
      </template>
    </sidebar>

    <snapshots-sidebar
      v-if="isSnapshotSidebar && mdAndUp"
      :snapshot-list="snapshotList"
      @on-close="toggleSnapshotSidebar"
      @on-delete="(snapshot: IVisitDocumentSnapshot) => openDeleteSnapshotPopup(snapshot, onDeleteSnapshot)"
    />
  </div>

  <meeting-peer-video
    v-if="isPinPMode && mainPeer"
    :id="mainPeer.id"
    class="peer-wrapper peer-wrapper_pip hover:tw-cursor-move tw-relative tw-max-w-[150px] md:tw-max-w-[235px] tw-w-full tw-h-full tw-touch-none"
    :peer="mainPeer"
    :is-displayed-peer-name="!((is1on1Meeting && mainPeer.roleName === EMeetingRole.HOST) || mainPeer.isLocal)"
    :is-audio="!!getAudioState(mainPeer)"
    :is-video="hmsStore.getState(selectIsPeerVideoEnabled(mainPeer.id))"
    is-maximize-button
    is-p-in-p-mode
    @on-maximize="closePinPModeHandler"
  />

  <div
    v-show="displaySnapshotFlyout && !isPinPMode"
    class="tw-absolute tw-z-20 tw-right-16 tw-bottom-32 tw-flex tw-flex-col tw-gap-8"
  >
    <div
      v-for="imageSrc in displayedSnapshotList"
      :key="imageSrc"
      class="tw-rounded-8 tw-border-2 tw-p-2 tw-bg-white tw-border-white"
      @click="openSnapshotPreview(imageSrc)"
    >
      <img :src="imageSrc" class="tw-max-h-[150px] tw-rounded-8" />
    </div>
  </div>

  <template v-if="isSnapshotPreview && isHostElement">
    <snapshot-preview
      :snapshot="selectedSnapshotForPreview"
      class="tw-absolute tw-z-30 md:tw-hidden"
      @on-save="isSnapshotPreview = false"
      @on-delete="openDeleteSnapshotPopup(selectedSnapshotForPreview, onFilterSnapshotHandler)"
    />
    <snapshot-preview-popup
      :is-shown-popup="isSnapshotPreview && mdAndUp"
      :snapshot="selectedSnapshotForPreview"
      class="tw-hidden md:tw-block"
      cancel-btn-text="Delete"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      submit-btn-text="Save snapshot"
      @close="openDeleteSnapshotPopup(selectedSnapshotForPreview, onFilterSnapshotHandler)"
      @submit="isSnapshotPreview = false"
    />
  </template>

  <mobile-snapshots-viewer
    v-if="isMobileSnapshotViewer"
    class="md:tw-hidden"
    :snapshot-list="snapshotList"
    @on-close="toggleMobileSnapshotViewer"
    @on-delete="(snapshot: IVisitDocumentSnapshot) => openDeleteSnapshotPopup(snapshot, onDeleteSnapshot)"
  />

  <popup
    :is-show="isShowDeleteSnapshotPopup"
    title="Delete snapshot"
    text="This action will delete the image and be undone. Are you sure you want to delete the snapshot?"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <template #footer>
      <div class="tw-text-center tw-flex tw-justify-around">
        <c-btn outlined @click="onCancelSnapshotPopupHandler">Cancel</c-btn>
        <c-btn accent @click="onDeleteSnapshotPopupHandler">Delete</c-btn>
      </div>
    </template>
  </popup>

  <popup
    :is-show="isShowRecordingPopup"
    title="Recording for quality purposes"
    text="This visit is being recorded for quality and improvement purposes. Click 'Acknowledge' to meet with a clinician. Otherwise click 'Leave' to cancel the visit"
    cancel-btn-text="Leave"
    submit-btn-text="Acknowledge"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="popupRecordingCancelHandler"
    @submit="popupRecordingSubmitHandler"
  />
  <popup
    :is-show="isShowRequestMicPopup"
    title="Unmute audio"
    text="Please unmute yourself so that we can hear you for this visit."
    cancel-btn-text="Stay muted"
    submit-btn-text="Unmute"
    @close="isShowRequestMicPopup = false"
    @submit="requestMicPopupHandler"
  />
  <popup
    :is-show="isShowRequestCameraPopup"
    title="Share video"
    text="Please share your video camera so that we can see you for this visit."
    cancel-btn-text="Stay off camera"
    submit-btn-text="Share video"
    @close="isShowRequestCameraPopup = false"
    @submit="requestCameraPopupHandler"
  />
  <popup
    :is-show="isShowLeaveMeetingPopup"
    is-close-cross-btn
    class="meeting-room__mobile-popup"
    title="End call"
    :text="leaveMeetingPopupCaption"
    @close="isShowLeaveMeetingPopup = false"
  >
    <template #footer>
      <div class="tw-flex tw-flex-col-reverse md:tw-flex-row tw-items-center tw-justify-center tw-gap-16">
        <template v-if="props.localRole === EMeetingRole.PATIENT">
          <c-btn class="tw-w-full" outlined @click="isShowLeaveMeetingPopup = false"> Don’t leave </c-btn>
          <c-btn class="tw-w-full" accent @click="leaveCall">Leave</c-btn>
        </template>
        <template v-if="props.localRole === EMeetingRole.HOST">
          <c-btn class="tw-w-full" outlined @click="leaveCall">Leave meeting</c-btn>
          <c-btn class="tw-w-full" accent @click="endCall">End call for all</c-btn>
        </template>
      </div>
    </template>
  </popup>
  <popup
    :is-show="isShowMediaSettingsPopup"
    is-close-cross-btn
    :is-bottom-controls="false"
    class="meeting-room__mobile-popup"
    title="Device settings"
    @close="isShowMediaSettingsPopup = false"
  >
    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-16">
        <c-select
          :value="localMediaSettings.videoInputDeviceId"
          class="tw-w-full"
          label="Video:"
          :options="formattedLocalVideoDeviceList"
          @change="updateLocalVideoSettings"
        />
        <c-select
          :value="localMediaSettings.audioInputDeviceId"
          class="tw-w-full"
          label="Microphone:"
          :options="formattedLocalAudioInDeviceList"
          @change="updateLocalAudioInputSettings"
        />
        <c-select
          v-if="formattedLocalAudioOutDeviceList.length"
          :value="localMediaSettings.audioOutputDeviceId"
          class="tw-w-full tw-hidden md:tw-block"
          label="Speakers:"
          :options="formattedLocalAudioOutDeviceList"
          @change="updateLocalAudioOutputSettings"
        />
      </div>
    </template>
  </popup>
</template>

<style lang="scss">
  .host {
    object-fit: cover;
  }
  .video-placeholder-text {
    &::before {
      content: 'Video off';
      color: white;
      display: inline-block;
    }
  }
  .meeting-room {
    &__mobile-popup.el-dialog {
      @apply tw-mb-[42px] md:tw-mb-auto;
    }
  }
  .peer-wrapper {
    &_pip:hover {
      .peer-wrapper {
        &__maximize {
          @apply tw-block;
        }
      }
    }
  }
  .main-container {
    &_1n1 {
      @apply md:tw-mx-auto md:tw-w-full;

      .peer-wrapper {
        @apply md:tw-mx-auto tw-h-full tw-w-auto;
      }
      video {
        @apply tw-w-full tw-h-full tw-object-cover tw-max-w-[960px]  md:tw-w-auto;
      }
    }
    &_multiple {
      @apply tw-h-full tw-mx-auto tw-w-full md:tw-w-auto;

      .peer-wrapper {
        @apply tw-h-full;
      }
      video {
        @apply tw-h-full tw-w-full tw-object-cover;
      }
    }
  }
  .peer-container {
    &_multiple {
      @apply tw-h-full tw-flex-shrink-0;
      video {
        @apply tw-h-full tw-w-full tw-object-cover;
      }
    }
    &_1n1 {
      .meeting-peer {
        @apply tw-rounded-8;
      }
      video {
        @apply tw-w-full tw-object-cover;
      }
    }
  }
</style>
